import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Drawer, Group, ScrollArea, Stack, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { contractInputSchema, type ContractInputSchema } from '../../lib/schemas';
import { useCreateContract, useUpdateContract } from '../../queries/contracts';
import { useContractFormLayout } from '../../queries/contract-form-layout';
import { useProjectStore } from '../../project/project-store';
import {
  CONTRACT_FORM_FIELDS,
  CONTRACT_FORM_FIELD_NAMES,
  CONTRACT_DEFAULT_LAYOUT,
} from '../../lib/form-layout/contract-fields';
import { FormLayoutRenderer } from '../../lib/form-layout/FormLayoutRenderer';
import { ApiError } from '../../lib/api-error';
import type { Contract } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  contract?: Contract | null;
  onSaved?: (c: Contract) => void;
}

const EMPTY: ContractInputSchema = {
  contract_type: 'row',
  status: 'draft',
  title: '',
  contract_number: null,
  effective_date: null,
  execution_date: null,
  expiration_date: null,
  termination_date: null,
  renewal_notice_date: null,
  recorded: false,
  recording_state: null,
  recording_county: null,
  recording_volume: null,
  recording_page: null,
  recording_instrument_number: null,
  recording_date: null,
  consideration_amount: null,
  consideration_currency: 'USD',
  consideration_notes: null,
  row_use: null,
  row_width_feet: null,
  row_length_feet: null,
  row_length_rods: null,
  row_pipeline_diameter_inches: null,
  row_pipeline_product: null,
  row_depth_restriction_feet: null,
  row_surface_restoration_required: null,
  township: null,
  range: null,
  section: null,
  block: null,
  lot: null,
  abstract: null,
  survey: null,
  apn: null,
  description: null,
  notes: null,
};

function fromContract(c: Contract): ContractInputSchema {
  const num = (v: string | number | null | undefined) =>
    v === null || v === undefined ? null : typeof v === 'string' ? Number(v) : v;
  return {
    contract_type: c.contract_type,
    status: c.status,
    title: c.title,
    contract_number: c.contract_number,
    effective_date: c.effective_date,
    execution_date: c.execution_date,
    expiration_date: c.expiration_date,
    termination_date: c.termination_date,
    renewal_notice_date: c.renewal_notice_date,
    recorded: c.recorded,
    recording_state: c.recording_state,
    recording_county: c.recording_county,
    recording_volume: c.recording_volume,
    recording_page: c.recording_page,
    recording_instrument_number: c.recording_instrument_number,
    recording_date: c.recording_date,
    consideration_amount: num(c.consideration_amount),
    consideration_currency: c.consideration_currency,
    consideration_notes: c.consideration_notes,
    row_use: c.row_use,
    row_width_feet: num(c.row_width_feet),
    row_length_feet: num(c.row_length_feet),
    row_length_rods: num(c.row_length_rods),
    row_pipeline_diameter_inches: num(c.row_pipeline_diameter_inches),
    row_pipeline_product: c.row_pipeline_product,
    row_depth_restriction_feet: num(c.row_depth_restriction_feet),
    row_surface_restoration_required: c.row_surface_restoration_required,
    township: c.township,
    range: c.range,
    section: c.section,
    block: c.block,
    lot: c.lot,
    abstract: c.abstract,
    survey: c.survey,
    apn: c.apn,
    description: c.description,
    notes: c.notes,
  };
}

export function ContractFormDrawer({ opened, onClose, contract, onSaved }: Props) {
  const isEdit = !!contract;
  const createM = useCreateContract();
  const updateM = useUpdateContract();
  const projectId = useProjectStore((s) => s.projectId);
  const layoutQ = useContractFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? CONTRACT_DEFAULT_LAYOUT;
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<ContractInputSchema>({
    resolver: zodResolver(contractInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${contract?.id ?? 'new'}-${contract?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(contract ? fromContract(contract) : EMPTY);
  }

  async function onSubmit(values: ContractInputSchema) {
    try {
      if (isEdit && contract) {
        const { contract_type: _omit, ...patch } = values;
        void _omit;
        const res = await updateM.mutateAsync({ id: contract.id, patch });
        notifications.show({ title: 'Contract saved', message: res.contract.title, color: 'pine' });
        onSaved?.(res.contract);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({
          title: 'Contract created',
          message: res.contract.title,
          color: 'pine',
        });
        onSaved?.(res.contract);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit contract' : 'New contract'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={CONTRACT_FORM_FIELDS}
            allNames={CONTRACT_FORM_FIELD_NAMES}
            context={{}}
            isEdit={isEdit}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create contract'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
